import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { apiCheckpointTemplateById } from "helpers/url/api/v2";
import { CheckpointTemplate } from "./models";

import { sharedTemplateKeys } from "../SharedTemplate/hooks";
import { toCreateCheckpointTemplatePayload, toUpdateCheckpointTemplatePayload } from "./helpers";
import { CheckpointTemplateSchema } from "./schema";

export const checkpointTemplateKeys = {
  all: ["checkpointTemplates"] as const,
  lists: () => [...checkpointTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...checkpointTemplateKeys.lists(), { filters }] as const,
  details: () => [...checkpointTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...checkpointTemplateKeys.details(), id] as const,
};

export function setNewCheckpointTemplateInQueryClient(
  queryClient: QueryClient,
  newCheckpointTemplate: CheckpointTemplate,
) {
  queryClient.setQueryData(
    checkpointTemplateKeys.detail(newCheckpointTemplate.id),
    (oldData: CheckpointTemplate | undefined) => {
      if (!oldData) {
        return newCheckpointTemplate;
      }
      return {
        ...oldData,
        ...newCheckpointTemplate,
      };
    },
  );
}

export const useCheckpointTemplateDetailQuery = (id: string | undefined, options: { enabled?: boolean } = {}) =>
  useQuery<CheckpointTemplate | null>({
    queryKey: checkpointTemplateKeys.detail(id as string),
    queryFn: async () => apiClient.get(apiCheckpointTemplateById(id as string)),
    staleTime: 1000 * 60 * 3,
    enabled: !!id && (options.enabled ?? true),
    ...options,
  });

export const useCheckpointTemplateListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<CheckpointTemplate[]>({
    queryKey: queryString ? checkpointTemplateKeys.list(queryString) : checkpointTemplateKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.checkpointTemplates.base)}${query}`),
    staleTime: 1000 * 60 * 3,
    ...options,
  });
};

function useCheckpointTemplateCreateRequest(
  mutationFn: (data: CheckpointTemplateSchema) => Promise<CheckpointTemplate>,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: checkpointTemplateKeys.all });
      setNewCheckpointTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

function useCheckpointTemplateUpdateRequest(
  mutationFn: (data: Partial<CheckpointTemplateSchema>) => Promise<CheckpointTemplate>,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: checkpointTemplateKeys.all });
      setNewCheckpointTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateCheckpointTemplate(checkpointTemplateId: string) {
  return useCheckpointTemplateUpdateRequest(
    async (formData: Partial<CheckpointTemplateSchema>): Promise<CheckpointTemplate> => {
      const payload = toUpdateCheckpointTemplatePayload(formData);
      return apiClient.put<CheckpointTemplate>(apiCheckpointTemplateById(checkpointTemplateId), {
        checkpointTemplate: payload,
      });
    },
  );
}

export function useCreateCheckpointTemplate() {
  return useCheckpointTemplateCreateRequest(async (formData: CheckpointTemplateSchema): Promise<CheckpointTemplate> => {
    const payload = toCreateCheckpointTemplatePayload(formData);
    return apiClient.post<CheckpointTemplate>(API_ROUTES.checkpointTemplates.base, { checkpointTemplate: payload });
  });
}
